import {
  string,
  object,
  number,
  boolean,
} from 'yup';
import { isValid } from 'iban';
import { VALIDATION_PROPERTIES, REG_EX, isValidNumber } from '../../../common/validation-rules';

const COUNTRY_TYPE_ID = 2;
const MAX_CHARACTERS_ALLOWED = 50;

const firmKeyDetailsValidationObject = {
  companyName: string()
    .max(MAX_CHARACTERS_ALLOWED, VALIDATION_PROPERTIES.maxAllow50characters)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  standardCompanyClassification: number()
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  noOfEmployees: number()
    .test('noOfEmployees', VALIDATION_PROPERTIES.invalid, (val) => (isValidNumber(val)))
    .positive(VALIDATION_PROPERTIES.invalid)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  noOfClients: number()
    .test('noOfClients', VALIDATION_PROPERTIES.invalid, (val) => (isValidNumber(val)))
    .positive(VALIDATION_PROPERTIES.invalid)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
};

const firmKeyDetailsFormSchemaEnvironment = ({ cocNumberRegex, cocNumberMessage }) => ({
  ...firmKeyDetailsValidationObject,
  kvkNumber: string()
    .matches(cocNumberRegex, cocNumberMessage)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
});

const firmKeyDetailsFormSchema = ({ cocNumberRegex, cocNumberMessage }) => object().shape(firmKeyDetailsFormSchemaEnvironment({ cocNumberRegex, cocNumberMessage }));

const firmAddressValidationObject = {
  countryId: number().when('countryTypeId', {
    is: (val) => val === COUNTRY_TYPE_ID,
    then: number().min(1, VALIDATION_PROPERTIES.selectOne).required(VALIDATION_PROPERTIES.required),
    otherwise: number().required(),
  }).nullable(),
  houseNumber: string()
    .matches(REG_EX.restrictSpecialCharacter, VALIDATION_PROPERTIES.noSpecialCharactersAllowed)
    .max(MAX_CHARACTERS_ALLOWED, VALIDATION_PROPERTIES.maxAllow50characters)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  streetName: string()
    .matches(REG_EX.restrictSpecialCharacter, VALIDATION_PROPERTIES.noSpecialCharactersAllowed)
    .max(MAX_CHARACTERS_ALLOWED, VALIDATION_PROPERTIES.maxAllow50characters)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  city: string()
    .matches(REG_EX.restrictSpecialCharacter, VALIDATION_PROPERTIES.noSpecialCharactersAllowed)
    .max(MAX_CHARACTERS_ALLOWED, VALIDATION_PROPERTIES.maxAllow50characters)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  email: string()
    .matches(REG_EX.email, VALIDATION_PROPERTIES.invalid)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
};

const firmAddressFormSchemaEnvironment = ({ postalCodeRegex, vatNumberRegex, telephoneNumberRegex }) => ({
  ...firmAddressValidationObject,
  postalCode: string()
    .matches(postalCodeRegex, VALIDATION_PROPERTIES.invalid)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  vatNumber: !!vatNumberRegex && string()
    .matches(vatNumberRegex, VALIDATION_PROPERTIES.invalid)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  phoneNumber: string()
    .matches(telephoneNumberRegex, VALIDATION_PROPERTIES.invalid)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
});

const firmAddressFormSchema = ({ postalCodeRegex, vatNumberRegex, telephoneNumberRegex }) => object().shape(firmAddressFormSchemaEnvironment({
  postalCodeRegex, vatNumberRegex, telephoneNumberRegex,
}));

const addEmployeeFormSchema = object().shape({
  email: string()
    .matches(REG_EX.email, VALIDATION_PROPERTIES.invalid)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  firstName: string()
    .matches(REG_EX.restrictSpecialCharacter, VALIDATION_PROPERTIES.noSpecialCharactersAllowed)
    .max(MAX_CHARACTERS_ALLOWED, VALIDATION_PROPERTIES.maxAllow50characters)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  lastName: string()
    .matches(REG_EX.restrictSpecialCharacter, VALIDATION_PROPERTIES.noSpecialCharactersAllowed)
    .max(MAX_CHARACTERS_ALLOWED, VALIDATION_PROPERTIES.maxAllow50characters)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  gender: number()
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
});

const otherSoftwaresUsedFormSchema = object().shape({
  administrationSoftwareId: number()
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  payrollSoftwareId: number()
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  taxationSoftwareId: number()
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
});

const invoiceInformationFormSchema = object().shape({
  accountNumber: string()
    .matches(REG_EX.restrictSpecialCharacter, VALIDATION_PROPERTIES.noSpecialCharactersAllowed)
    .test('IBAN', VALIDATION_PROPERTIES.invalid, (val) => (isValid(val)))
    .nullable(),
  invoiceEmail: string()
    .matches(REG_EX.email, VALIDATION_PROPERTIES.invalid)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  bankName: string()
    .matches(REG_EX.allowOnlyAlphabets, VALIDATION_PROPERTIES.invalid)
    .max(50, VALIDATION_PROPERTIES.maxAllow50characters)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
  bankAccountName: string()
    .matches(REG_EX.alphabetsDigitsAndSpecialCharacters, VALIDATION_PROPERTIES.invalid)
    .max(50, VALIDATION_PROPERTIES.maxAllow50characters)
    .required(VALIDATION_PROPERTIES.required)
    .nullable(),
});

const reviewFormSchema = object().shape({
  isPartnerTermsAccepted: boolean()
    .test('isPartnerTermsAccepted', VALIDATION_PROPERTIES.required, (val) => val),
});

export {
  firmKeyDetailsFormSchema,
  firmAddressFormSchema,
  addEmployeeFormSchema,
  otherSoftwaresUsedFormSchema,
  invoiceInformationFormSchema,
  reviewFormSchema,
};
